/*
 * ALOHA QUEEN SUPERNOVA
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - NO UNAUTHORIZED USE
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Sparkles, Zap, Shield, Crown, Star, Settings, Eye, EyeOff } from 'lucide-react';
import CosmicWallpaperBackground from '../components/CosmicWallpaperBackground';
import Enhanced8KGalaxyBackground from '../components/Enhanced8KGalaxyBackground';
import SupernovaBackground from '../components/SupernovaBackground';
import SolarSystemBackground from '../components/SolarSystemBackground';
import DynamicGradientCard from '../components/DynamicGradientCard';
import AnimatedTitle from '../components/AnimatedTitle';

const Home = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [qualityLevel, setQualityLevel] = useState('high');
  const [show3DEffects, setShow3DEffects] = useState(true);
  const [showBackgroundEffects, setShowBackgroundEffects] = useState(true);
  const [backgroundType, setBackgroundType] = useState('solar'); // solar, supernova, wallpaper, galaxy

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
      {/* Background Selection */}
      {showBackgroundEffects && (
        <>
          {backgroundType === 'solar' && <SolarSystemBackground />}
          {backgroundType === 'supernova' && <SupernovaBackground effectsOn={show3DEffects} resolution={qualityLevel} />}
          {backgroundType === 'wallpaper' && <CosmicWallpaperBackground autoPlay={true} transitionSpeed={10000} />}
          {backgroundType === 'galaxy' && <Enhanced8KGalaxyBackground quality={qualityLevel} />}
        </>
      )}
      
      {/* Quick 3D Graphics Toggle - VISIBLE on Main Page */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        <button
          onClick={() => setShowBackgroundEffects(!showBackgroundEffects)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold shadow-xl transition ${
            showBackgroundEffects 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
          }`}
          title="Toggle 3D Background"
        >
          {showBackgroundEffects ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          3D Graphics: {showBackgroundEffects ? 'ON' : 'OFF'}
        </button>
        
        <button
          onClick={() => setShow3DEffects(!show3DEffects)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold shadow-xl transition ${
            show3DEffects 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
          }`}
          title="Toggle 3D Effects"
        >
          <Zap className="w-5 h-5" />
          Effects: {show3DEffects ? 'ON' : 'OFF'}
        </button>
      </div>
      
      {/* Settings Toggle Button */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        className="fixed top-4 right-4 z-50 p-3 bg-gray-900/70 hover:bg-gray-900/90 backdrop-blur-md rounded-full text-white shadow-xl transition"
        title="Advanced Settings"
      >
        <Settings className="w-6 h-6" />
      </button>

      {/* Settings Panel */}
      {showSettings && (
        <div className="fixed top-20 right-4 z-50 bg-gray-900/90 backdrop-blur-xl p-6 rounded-2xl shadow-2xl border border-purple-500/30 w-80">
          <h3 className="text-xl font-bold text-white mb-4">Display Settings</h3>
          
          {/* Quality Selector */}
          <div className="mb-4">
            <label className="text-gray-300 text-sm mb-2 block">Graphics Quality</label>
            <select
              value={qualityLevel}
              onChange={(e) => setQualityLevel(e.target.value)}
              className="w-full p-2 bg-gray-800 text-white rounded-lg border border-gray-700"
            >
              <option value="auto">Auto-Tune (Default)</option>
              <option value="480">480p (Low)</option>
              <option value="720">720p (Medium)</option>
              <option value="1080">1080p (High)</option>
              <option value="4k">4K (Extreme)</option>
              <option value="8k">8K (Ludicrous)</option>
            </select>
          </div>

          {/* Background Type Selector */}
          <div className="mb-4">
            <label className="text-gray-300 text-sm mb-2 block">Background Type</label>
            <select
              value={backgroundType}
              onChange={(e) => setBackgroundType(e.target.value)}
              className="w-full p-2 bg-gray-800 text-white rounded-lg border border-gray-700"
            >
              <option value="solar">Solar System (Planets & Cosmos)</option>
              <option value="supernova">Supernova Loop (60s with explosion)</option>
              <option value="wallpaper">Cosmic Wallpaper (Photo)</option>
              <option value="galaxy">Enhanced Galaxy (Planets)</option>
            </select>
          </div>

          {/* 3D Effects Toggle */}
          <div className="mb-4 flex items-center justify-between">
            <label className="text-gray-300 text-sm">3D Effects</label>
            <button
              onClick={() => setShow3DEffects(!show3DEffects)}
              className={`w-14 h-7 rounded-full transition ${show3DEffects ? 'bg-green-500' : 'bg-gray-600'}`}
            >
              <div className={`w-6 h-6 bg-white rounded-full transition-transform ${show3DEffects ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Background Effects Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-gray-300 text-sm">Background Animation</label>
            <button
              onClick={() => setShowBackgroundEffects(!showBackgroundEffects)}
              className={`w-14 h-7 rounded-full transition ${showBackgroundEffects ? 'bg-green-500' : 'bg-gray-600'}`}
            >
              <div className={`w-6 h-6 bg-white rounded-full transition-transform ${showBackgroundEffects ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
      )}
      
      {/* Content Overlay with Reduced Opacity */}
      <div className="relative z-10">
        {/* Hero Section - Expanded with Better Spacing */}
        <div className="container mx-auto px-6 py-32 max-w-7xl">
          <div className="text-center mb-24">
            {/* Hawaiian Welcome */}
            <div className="mb-8 animate-fade-in">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Crown className="w-12 h-12 text-yellow-400 animate-bounce" />
                <p className="text-5xl md:text-6xl font-bold text-yellow-300 tracking-wide" style={{textShadow: '0 0 30px rgba(255,215,0,0.7)'}}>
                  ʻAloha ʻE Komo Mai
                </p>
                <Crown className="w-12 h-12 text-yellow-400 animate-bounce" style={{animationDelay: '0.2s'}} />
              </div>
              <p className="text-2xl text-purple-300 italic mb-3">Welcome to</p>
            </div>
            
            {/* Main Title with Animation */}
            <div className="mb-10">
              <AnimatedTitle className="text-7xl md:text-9xl mb-6">
                Queen Supernova
              </AnimatedTitle>
              <div className="flex items-center justify-center gap-3 text-gray-300">
                <Star className="w-6 h-6 text-yellow-400 animate-spin" style={{animationDuration: '3s'}} />
                <p className="text-base font-semibold tracking-widest">POWERED BY ADVANCED AI AGENTS</p>
                <Star className="w-6 h-6 text-yellow-400 animate-spin" style={{animationDuration: '3s', animationDirection: 'reverse'}} />
              </div>
            </div>
            
            <p className="text-3xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed">
              Experience the future with <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-500">3D AI Agents</span>, 
              <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500"> Voice Synthesis</span>, and 
              <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-blue-500"> Intelligent Conversations</span>
            </p>
            
            <div className="flex items-center justify-center gap-8 mb-16">
              <Link
                to="/signup"
                className="group relative px-10 py-5 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-600 hover:to-red-600 text-white text-2xl font-bold rounded-2xl transition-all transform hover:scale-110 shadow-2xl"
                data-testid="get-started-button"
              >
                <span className="relative z-10">Start Free Now</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity" />
                <Sparkles className="inline-block ml-3 w-7 h-7 animate-pulse" />
              </Link>
              <Link
                to="/pricing"
                className="px-10 py-5 bg-purple-600/40 hover:bg-purple-600/60 border-2 border-purple-400 text-white text-2xl font-bold rounded-2xl transition-all backdrop-blur-lg transform hover:scale-105 shadow-xl"
                data-testid="view-pricing-button"
              >
                View Plans
              </Link>
            </div>

            {/* Avatar Preview with Plans - NEW */}
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
              {/* Basic Plan */}
              <div className="bg-gray-900/40 backdrop-blur-md p-6 rounded-2xl border border-purple-500/20 hover:border-purple-500/50 transition">
                <div className="text-6xl mb-4">👑</div>
                <h3 className="text-2xl font-bold text-white mb-2">Basic</h3>
                <p className="text-purple-300 mb-4">$9.99/mo</p>
                <ul className="text-gray-300 text-sm space-y-2">
                  <li>✓ Queen Supernova</li>
                  <li>✓ 50 Daily Prompts</li>
                  <li>✓ Voice Synthesis</li>
                </ul>
              </div>

              {/* Premium Plan - Highlighted */}
              <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 backdrop-blur-md p-6 rounded-2xl border-2 border-yellow-400 transform scale-105 shadow-2xl">
                <div className="text-6xl mb-4">🌟</div>
                <div className="bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">POPULAR</div>
                <h3 className="text-2xl font-bold text-white mb-2">Premium</h3>
                <p className="text-yellow-300 mb-4 text-xl font-bold">$49.99/mo</p>
                <ul className="text-gray-200 space-y-2">
                  <li>✓ All Avatars</li>
                  <li>✓ Unlimited Prompts</li>
                  <li>✓ Premium Voices</li>
                  <li>✓ Priority Support</li>
                </ul>
              </div>

              {/* Enterprise Plan */}
              <div className="bg-gray-900/40 backdrop-blur-md p-6 rounded-2xl border border-purple-500/20 hover:border-purple-500/50 transition">
                <div className="text-6xl mb-4">🚀</div>
                <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
                <p className="text-purple-300 mb-4">$99.99/mo</p>
                <ul className="text-gray-300 text-sm space-y-2">
                  <li>✓ Everything</li>
                  <li>✓ API Access</li>
                  <li>✓ Custom Training</li>
                </ul>
              </div>
            </div>

            <Link
              to="/pricing"
              className="inline-block text-purple-300 hover:text-purple-200 text-lg underline"
            >
              View More Plans →
            </Link>
          </div>
        </div>

        {/* Features Grid - Reduced Opacity */}
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
            <DynamicGradientCard gradient="royal" className="text-center bg-gray-900/50 backdrop-blur-md" data-testid="feature-avatar-chat">
              <MessageCircle className="w-16 h-16 text-purple-300 mx-auto mb-4 animate-pulse" />
              <h3 className="text-2xl font-bold text-white mb-3">3D Avatar Chat</h3>
              <p className="text-gray-300 leading-relaxed">
                Interact with photorealistic 3D avatars powered by advanced AI
              </p>
            </DynamicGradientCard>

          <DynamicGradientCard gradient="gold" className="text-center bg-gray-900/50 backdrop-blur-md" data-testid="feature-voice">
            <Sparkles className="w-16 h-16 text-pink-300 mx-auto mb-4 animate-pulse" style={{animationDelay: '0.2s'}} />
            <h3 className="text-2xl font-bold text-white mb-3">Voice Synthesis</h3>
            <p className="text-gray-300 leading-relaxed">
              Ultra-realistic AI voice with perfect lip synchronization
            </p>
          </DynamicGradientCard>

          <DynamicGradientCard gradient="neon" className="text-center bg-gray-900/50 backdrop-blur-md" data-testid="feature-ai">
            <Zap className="w-16 h-16 text-yellow-300 mx-auto mb-4 animate-pulse" style={{animationDelay: '0.4s'}} />
            <h3 className="text-2xl font-bold text-white mb-3">Advanced AI</h3>
            <p className="text-gray-300 leading-relaxed">
              Powered by latest Gemini, Claude & GPT models for intelligent responses
            </p>
          </DynamicGradientCard>

          <DynamicGradientCard gradient="ocean" className="text-center bg-gray-900/50 backdrop-blur-md" data-testid="feature-secure">
            <Shield className="w-16 h-16 text-green-300 mx-auto mb-4 animate-pulse" style={{animationDelay: '0.6s'}} />
            <h3 className="text-2xl font-bold text-white mb-3">Secure & Private</h3>
            <p className="text-gray-300 leading-relaxed">
              Enterprise-grade security protects your data and conversations
            </p>
          </DynamicGradientCard>
        </div>

        {/* NEW: Streaming Platform Showcase */}
        <div className="container mx-auto px-6 max-w-7xl mb-24">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              📺 Mimi's All-in-One Streaming Hub
            </h2>
            <p className="text-xl text-gray-300">
              AI-Powered Content Discovery • Trending Videos • Personalized Recommendations
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 backdrop-blur-xl rounded-2xl p-8 border border-blue-500/30">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700 hover:border-blue-500 transition-all">
                <div className="text-5xl mb-4">🤖</div>
                <h3 className="text-xl font-bold text-white mb-2">AI Recommendations</h3>
                <p className="text-gray-400 text-sm">
                  4 advanced algorithms analyze your taste to suggest perfect content
                </p>
              </div>
              
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700 hover:border-red-500 transition-all">
                <div className="text-5xl mb-4">🔥</div>
                <h3 className="text-xl font-bold text-white mb-2">Trending Content</h3>
                <p className="text-gray-400 text-sm">
                  Real-time trending analysis with view velocity tracking
                </p>
              </div>
              
              <div className="bg-gray-900/50 p-6 rounded-xl border border-green-500 transition-all">
                <div className="text-5xl mb-4">📋</div>
                <h3 className="text-xl font-bold text-white mb-2">Smart Watchlist</h3>
                <p className="text-gray-400 text-sm">
                  Manage favorites and track your viewing progress
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <Link
                to="/streaming"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-lg font-bold rounded-xl transition transform hover:scale-105 shadow-lg"
              >
                Explore Streaming Hub →
              </Link>
            </div>
          </div>
        </div>

        {/* Gaming Platform Showcase */}
        <div className="container mx-auto px-6 max-w-7xl mb-24">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
              🎮 Milkman's Twisted Universe
            </h2>
            <p className="text-xl text-gray-300">
              Vehicular Combat • Skill Trees • Epic Battles
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-red-900/40 to-orange-900/40 backdrop-blur-xl rounded-2xl p-8 border border-red-500/30">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700 hover:border-yellow-500 transition-all">
                <div className="text-5xl mb-4">🌳</div>
                <h3 className="text-xl font-bold text-white mb-2">18 Unique Skills</h3>
                <p className="text-gray-400 text-sm">
                  Offensive, Defensive & Support skill trees to customize your playstyle
                </p>
              </div>
              
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700 hover:border-green-500 transition-all">
                <div className="text-5xl mb-4">🏆</div>
                <h3 className="text-xl font-bold text-white mb-2">100 Level Progression</h3>
                <p className="text-gray-400 text-sm">
                  Advance from Rookie to Milkman Supreme with rewards at every level
                </p>
              </div>
              
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700 hover:border-purple-500 transition-all">
                <div className="text-5xl mb-4">⚔️</div>
                <h3 className="text-xl font-bold text-white mb-2">Epic Battles</h3>
                <p className="text-gray-400 text-sm">
                  Multiple game modes with real-time leaderboards and rankings
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <Link
                to="/game"
                className="inline-block px-8 py-4 bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white text-lg font-bold rounded-xl transition transform hover:scale-105 shadow-lg"
              >
                Enter Battle Arena →
              </Link>
            </div>
          </div>
        </div>

        {/* CTA Section - Compact */}
        <div className="bg-gradient-to-r from-purple-900/60 to-blue-900/60 backdrop-blur-xl rounded-2xl p-12 text-center border border-white/10 shadow-2xl mx-auto max-w-4xl mb-24">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Experience the Future?</h2>
          <p className="text-xl text-gray-200 mb-8">
            Start free or unlock premium features with Queen Supernova
          </p>
          <Link
            to="/signup"
            className="inline-block px-10 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white text-xl font-bold rounded-xl transition transform hover:scale-105 shadow-lg"
            data-testid="cta-signup-button"
          >
            Start Your Journey
          </Link>
        </div>
      </div>
      </div>
      
      {/* Copyright Footer - Small at Bottom */}
      <footer className="relative z-10 py-6 text-center border-t border-white/10 bg-gray-900/80 backdrop-blur-sm">
        <p className="text-xs text-gray-400">
          © 2025 Malcolm KH Lee III. All Rights Reserved. | Aloha Queen Supernova Platform
        </p>
        <p className="text-xs text-gray-500 mt-1">
          PROPRIETARY AND CONFIDENTIAL - Patent Pending
        </p>
      </footer>
    </div>
  );
};

export default Home;
